import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { CalendarIcon, ClockIcon, SparklesIcon } from '@heroicons/react/16/solid';
import { Dialog } from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import {
  AmenityAvailabilityReturn,
  AmenityReturn,
  HoaReturn,
  ReservationPreviewReturn,
  ReservationReturn,
  ReservationRuleCode,
  ReservationRuleViolation,
  ReservationStaffCreate,
} from '../clients/rust-api-client';
import { amenityService, reservationService } from '../clients/ApiClients';
import { formatMoney } from '../utils/billingHelpers';
import { DatePicker, PropertyPicker } from '../violation/linear/PropertyPicker';
import { CreateScope } from '../shell/createScope';
import { ScopeChips, useForHoa, useScopeState } from '../shell/ScopeChips';
import { RuleViolationsPanel } from './RuleViolationsPanel';
import { FeeBreakdown } from './ReservationOperations';
import {
  addDays,
  canSubmitOverrides,
  formatDayLabel,
  formatDuration,
  formatMinute,
  formatNights,
  formatWindows,
  hhmmToMinute,
  isZeroMoney,
  localMinute,
  nightOptions,
  reservationErrorMessage,
  ruleViolationsFrom,
  stayTimes,
  todayIn,
  toLocal,
  tzLabel,
} from './reservationMeta';
import { checkboxClass, inputClass, labelClass } from './formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { FieldPicker } from '../common/Custom/FieldPicker';

/**
 * Book an amenity on a household's behalf (#642), as a Linear-style
 * create modal: where (association, home) and what (amenity, day) as
 * property chips, then the start time from the amenity's real
 * availability, then a live preview of the fee and of every rule the
 * booking would break.
 *
 * A manager booking is the decision itself, so it is approved on create
 * and its fee posts straight away. Rules are overridden **one code at a
 * time** with a reason, never with a blanket force; a rule that can never
 * be overridden (the slot is taken) blocks the button.
 *
 * Phase 2: an **overnight** amenity is booked by arrival day and nights
 * (check-in and check-out are the amenity's own times), a
 * **shared-capacity** one shows how many places each start time has left,
 * and an amenity that needs the vehicle asks for the plate.
 */
export interface ReservationPrefill {
  amenityId?: string;
  /** 'YYYY-MM-DD' in the amenity's zone. */
  day?: string;
  /** Minutes since local midnight. */
  minute?: number;
}

interface Props {
  open: boolean;
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  scope?: CreateScope;
  lockScope?: boolean;
  prefill?: ReservationPrefill;
  onClose: () => void;
  onCreated: (reservation: ReservationReturn) => void;
}

const fetchAmenities = async (hoaId: string): Promise<AmenityReturn[]> =>
  (await amenityService.listAmenities({ hoaId })).data.amenities
    .filter((a) => a.is_active && a.booking_mode !== 'info_only')
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));

const slotClass: Record<string, string> = {
  available: 'border-line text-fg-secondary hover:bg-fg/5',
  booked: 'border-line/50 text-fg-faint line-through cursor-not-allowed',
  past: 'border-line/50 text-fg-faint cursor-not-allowed',
  closed: 'border-warning/40 text-warning',
  too_soon: 'border-warning/40 text-warning',
  too_far: 'border-warning/40 text-warning',
  full: 'border-line/50 text-fg-faint line-through cursor-not-allowed',
  out_of_season: 'border-warning/40 text-warning',
};

const slotTitle: Record<string, string> = {
  available: 'Available',
  booked: 'Already booked',
  past: 'In the past',
  closed: 'During a closure — needs an override',
  too_soon: 'Less notice than the rules allow — needs an override',
  too_far: 'Further ahead than the rules allow — needs an override',
  full: 'No places left',
  out_of_season: 'Out of season — needs an override',
};

const nightClass: Record<string, string> = {
  available: 'bg-accent/15 text-fg-secondary',
  booked: 'bg-danger/15 text-danger line-through',
  closed: 'bg-warning/15 text-warning',
  out_of_season: 'bg-warning/15 text-warning',
  past: 'bg-fg/5 text-fg-faint',
  too_soon: 'bg-warning/15 text-warning',
  too_far: 'bg-warning/15 text-warning',
  full: 'bg-danger/15 text-danger',
};

export const CreateReservationModal: React.FC<Props> = ({
  open,
  hoas,
  hoasLoading,
  scope = {},
  lockScope,
  prefill,
  onClose,
  onCreated,
}) => {
  const { hoaId, setHoaId, homeId, setHomeId } = useScopeState(open, scope, hoas);
  const amenities = useForHoa(hoaId, fetchAmenities, 'amenities');
  const [amenityId, setAmenityId] = useState<string | null>(null);
  const [day, setDay] = useState('');
  const [startMinute, setStartMinute] = useState<number | null>(null);
  const [customStart, setCustomStart] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [partySize, setPartySize] = useState('1');
  const [purpose, setPurpose] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [nights, setNights] = useState(1);
  const [plate, setPlate] = useState('');
  const [plateState, setPlateState] = useState('');
  const [vehicleDescription, setVehicleDescription] = useState('');
  const [availability, setAvailability] = useState<AmenityAvailabilityReturn | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReservationPreviewReturn | null>(null);
  const [violations, setViolations] = useState<ReservationRuleViolation[]>([]);
  const [ticked, setTicked] = useState<string[]>([]);
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amenity = amenities.items.find((a) => a.id === amenityId) ?? null;
  const overnight = amenity?.booking_mode === 'overnight';
  const shared = amenity?.booking_mode === 'shared_capacity';

  // Reset on open, from the prefill when there is one (a click on an
  // empty calendar cell).
  useEffect(() => {
    if (!open) return;
    setAmenityId(prefill?.amenityId ?? null);
    setDay(prefill?.day ?? '');
    setStartMinute(prefill?.minute ?? null);
    setCustomStart('');
    setDuration(null);
    setPartySize('1');
    setPurpose('');
    setInternalNotes('');
    setAgreementSigned(false);
    setNights(1);
    setPlate('');
    setPlateState('');
    setVehicleDescription('');
    setPreview(null);
    setViolations([]);
    setTicked([]);
    setOverrideReason('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // An amenity belongs to one association.
  useEffect(() => {
    if (amenityId && !amenities.loading && amenities.items.length && !amenity) {
      setAmenityId(null);
    }
  }, [amenityId, amenity, amenities.loading, amenities.items.length]);

  // Default the day to today in the amenity's zone, and the duration to
  // its minimum.
  useEffect(() => {
    if (!amenity) return;
    if (!day) setDay(todayIn(amenity.timezone));
    setDuration((d) =>
      d && d >= amenity.min_duration_minutes && d <= amenity.max_duration_minutes
        ? d
        : amenity.min_duration_minutes
    );
    setNights((n) => {
      const options = nightOptions(amenity.min_nights, amenity.max_nights);
      return options.includes(n) ? n : options[0];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenity?.id]);

  // What the day looks like for this home.
  useEffect(() => {
    setAvailability(null);
    setAvailabilityError(null);
    if (!open || !amenityId || !day) return;
    let live = true;
    amenityService
      .getAmenityAvailability({
        amenityId,
        date: day,
        // A stay: every night of it, so each one's status shows.
        to: overnight ? addDays(day, Math.max(0, nights - 1)) : undefined,
        homeId: homeId ?? undefined,
      })
      .then((r) => live && setAvailability(r.data))
      .catch((err) => live && setAvailabilityError(reservationErrorMessage(err, "Couldn't load availability.")));
    return () => {
      live = false;
    };
  }, [open, amenityId, day, homeId, overnight, nights]);

  const start = customStart ? hhmmToMinute(customStart) : startMinute;
  const durations = useMemo(() => {
    if (!amenity) return [];
    const out: number[] = [];
    for (
      let m = amenity.min_duration_minutes;
      m <= amenity.max_duration_minutes;
      m += amenity.slot_increment_minutes
    ) {
      out.push(m);
    }
    return out;
  }, [amenity]);

  const party = Number(partySize);
  const partyOk = /^\d+$/.test(partySize) && party >= 1;
  const agreement = amenity?.requires_agreement ? amenity.current_agreement ?? null : null;

  const times =
    overnight && amenity?.check_in_minute != null && amenity.check_out_minute != null
      ? stayTimes(day, nights, amenity.check_in_minute, amenity.check_out_minute)
      : !overnight && start !== null && duration
        ? { start_local: toLocal(day, start), end_local: toLocal(day, start + duration) }
        : null;
  const request: ReservationStaffCreate | null =
    hoaId && homeId && amenityId && day && times && partyOk
      ? {
          amenity_id: amenityId,
          home_id: homeId,
          ...times,
          party_size: party,
          purpose: purpose.trim() || null,
          internal_notes: internalNotes.trim() || null,
          agreement_id: agreement && agreementSigned ? agreement.id : null,
          vehicle_plate: amenity?.requires_vehicle ? plate.trim() || null : null,
          vehicle_state: amenity?.requires_vehicle ? plateState.trim() || null : null,
          vehicle_description: amenity?.requires_vehicle ? vehicleDescription.trim() || null : null,
        }
      : null;
  const requestKey = request ? JSON.stringify(request) : '';

  // Live preview: the fee, and every rule the booking breaks as it stands.
  useEffect(() => {
    setPreview(null);
    setError(null);
    if (!open || !request) {
      setViolations([]);
      return;
    }
    let live = true;
    const t = window.setTimeout(() => {
      reservationService
        .previewReservation({ reservationStaffCreate: request })
        .then((r) => {
          if (!live) return;
          setPreview(r.data);
          setViolations(r.data.violations);
          setTicked((codes) => codes.filter((c) => r.data.violations.some((v) => v.code === c)));
        })
        .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't check the booking.")));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
    // The request is rebuilt every render; its serialised form is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestKey]);

  const overridesOk = canSubmitOverrides(violations, ticked, overrideReason);
  const canSubmit = !!request && !!preview && overridesOk && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || !preview || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.createReservation({
        reservationStaffCreate: {
          ...request,
          quoted_fee: preview.fee,
          overrides: ticked.length ? (ticked as ReservationRuleCode[]) : null,
          override_reason: ticked.length ? overrideReason.trim() : null,
        },
      });
      onCreated(res.data);
      onClose();
    } catch (err) {
      const broken = ruleViolationsFrom(err);
      if (broken) {
        setViolations(broken);
      } else {
        setError(reservationErrorMessage(err, "Couldn't create the reservation."));
      }
    } finally {
      setSaving(false);
    }
  };

  const muted = (text: string) => <span className="text-fg-muted">{text}</span>;
  const thisYear = new Date().getFullYear();

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <form onSubmit={submit} aria-label="New reservation">
        <p className="text-[12px] font-medium text-fg-muted">New reservation</p>
        <p className="mt-2 text-xl font-semibold text-fg">
          {amenity ? amenity.name : 'Book an amenity for a household'}
        </p>
        <p className="mt-1 text-[13px] text-fg-muted">
          Booked by management, so it is approved as soon as it is created and any fee posts to the home’s account.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <ScopeChips
            hoas={hoas}
            hoasLoading={hoasLoading}
            hoaId={hoaId}
            onHoaChange={setHoaId}
            lockHoa={lockScope && !!scope.hoaId}
            memberId={scope.memberId}
            home={{ homeId, onChange: setHomeId, locked: lockScope && !!scope.homeId }}
          />
          <PropertyPicker
            options={amenities.items.map((a) => ({ value: a.id, label: a.name }))}
            value={amenityId}
            onChange={(id) => {
              setAmenityId(id);
              setStartMinute(null);
              setCustomStart('');
            }}
            placeholder="Amenity…"
            ariaLabel={amenity ? `Amenity: ${amenity.name}` : 'Choose amenity'}
            readOnly={!hoaId || amenities.loading}
            trigger={
              <>
                <SparklesIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {amenity
                  ? amenity.name
                  : muted(!hoaId ? 'Amenity' : amenities.loading ? 'Loading amenities…' : amenities.items.length ? 'Amenity' : 'No bookable amenities')}
              </>
            }
          />
          <DatePicker
            value={day}
            onChange={(d) => {
              setDay(d);
              setStartMinute(null);
            }}
            ariaLabel={day ? `Day: ${formatDayLabel(day, thisYear)}` : 'Choose day'}
            readOnly={!amenity}
            trigger={
              <>
                <CalendarIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {day ? formatDayLabel(day, thisYear) : muted('Day')}
              </>
            }
          />
        </div>

        {amenity && day && (
          <div className="mt-4 space-y-3">
            <p className="flex flex-wrap items-center gap-x-3 text-[12px] text-fg-muted">
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="h-3.5 w-3.5" aria-hidden />
                {availability ? formatWindows(availability.open_windows) : '…'}
              </span>
              <span>{tzLabel(amenity.timezone)}</span>
              {availability?.home_usage && (
                <span>
                  This household: {availability.home_usage.day} today · {availability.home_usage.week} this week ·{' '}
                  {availability.home_usage.active} upcoming
                </span>
              )}
            </p>
            <ErrorAlert>{availabilityError}</ErrorAlert>
            {overnight && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-end gap-3">
                  <div className={labelClass}>
                    Nights
                    <FieldPicker
                      size="sm"
                      className="w-32"
                      label="Nights"
                      value={String(nights)}
                      onChange={(v) => setNights(Number(v))}
                      options={nightOptions(amenity.min_nights, amenity.max_nights).map((n) => ({
                        value: String(n),
                        label: formatNights(n),
                      }))}
                    />
                  </div>
                  {amenity.check_in_minute != null && amenity.check_out_minute != null && (
                    <p className="pb-1.5 text-[13px] text-fg-secondary">
                      Check-in {formatDayLabel(day, thisYear)}, {formatMinute(amenity.check_in_minute)} · check-out{' '}
                      {formatDayLabel(addDays(day, nights), thisYear)}, {formatMinute(amenity.check_out_minute)}
                    </p>
                  )}
                </div>
                {amenity.check_in_minute == null && (
                  <ErrorAlert>This amenity has no check-in time yet — set it on its Booking limits tab.</ErrorAlert>
                )}
                {availability && availability.nights.length > 0 && (
                  <ul aria-label="Nights" className="flex flex-wrap gap-1.5">
                    {availability.nights.map((n) => (
                      <li
                        key={n.date}
                        className={clsx('rounded-md px-2 py-1 text-[12px]', nightClass[n.status] ?? nightClass.available)}
                        title={slotTitle[n.status] ?? n.status}
                      >
                        {formatDayLabel(n.date, thisYear)}
                        {!isZeroMoney(n.fee) && ` · ${formatMoney(n.fee)}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {availability && !overnight && (
              <fieldset>
                <legend className="mb-1.5 text-[12px] font-medium text-fg-muted">Start time</legend>
                {availability.starts.length === 0 ? (
                  <p className="text-[13px] text-fg-muted">
                    {availability.hours_configured
                      ? 'No start times on this day.'
                      : 'This amenity has no opening hours yet — set them on its page, or pick another time below.'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {availability.starts.map((s) => {
                      const m = localMinute(s.start_local);
                      if (m === null) return null;
                      const disabled = s.status === 'booked' || s.status === 'past';
                      const chosen = !customStart && startMinute === m;
                      return (
                        <button
                          key={s.start_local}
                          type="button"
                          disabled={disabled}
                          title={slotTitle[s.status] ?? s.status}
                          aria-pressed={chosen}
                          onClick={() => {
                            setCustomStart('');
                            setStartMinute(m);
                          }}
                          className={clsx(
                            'rounded-md border px-2 py-1 text-[12px] tabular-nums',
                            slotClass[s.status] ?? slotClass.available,
                            chosen && '!border-accent !bg-accent/15 !text-fg'
                          )}
                        >
                          {formatMinute(m)}
                          {shared && s.remaining_capacity != null && (
                            <span className="ml-1 text-fg-muted">· {s.remaining_capacity} left</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <label className="mt-2 flex items-center gap-2 text-[12px] text-fg-muted">
                  Or another start time
                  <input
                    type="time"
                    step={amenity.slot_increment_minutes * 60}
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className={inputClass}
                  />
                </label>
              </fieldset>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div className={clsx(labelClass, overnight && 'hidden')}>
                Duration
                <FieldPicker
                  size="sm"
                  className="w-36"
                  label="Duration"
                  value={duration == null ? null : String(duration)}
                  onChange={(v) => setDuration(Number(v))}
                  options={durations.map((d) => ({ value: String(d), label: formatDuration(d) }))}
                />
              </div>
              <label className={labelClass}>
                Party size
                <input
                  inputMode="numeric"
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value.replace(/\D/g, ''))}
                  aria-invalid={!partyOk}
                  className={clsx(inputClass, 'w-20', !partyOk && 'border-danger')}
                />
              </label>
              {!overnight && start !== null && duration && (
                <p className="pb-1.5 text-[13px] text-fg-secondary">
                  {formatMinute(start)} – {formatMinute(start + duration)}
                  {start + duration > 1440 && ' (next day)'}
                </p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className={labelClass}>
                Purpose (optional)
                <input
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. Birthday party"
                  className={inputClass}
                />
              </label>
              <label className={labelClass}>
                Internal note (managers only)
                <input
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>

            {amenity.requires_vehicle && (
              <div className="grid gap-3 sm:grid-cols-[1fr_5rem_1fr]">
                <label className={labelClass}>
                  Licence plate
                  <input
                    value={plate}
                    onChange={(e) => setPlate(e.target.value.toUpperCase())}
                    placeholder="ABC1234"
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  State
                  <input
                    value={plateState}
                    onChange={(e) => setPlateState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="CO"
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Vehicle (optional)
                  <input
                    value={vehicleDescription}
                    onChange={(e) => setVehicleDescription(e.target.value)}
                    placeholder="e.g. Blue Subaru Outback"
                    className={inputClass}
                  />
                </label>
              </div>
            )}

            {agreement && (
              <label className="flex items-start gap-2 text-[13px] text-fg-secondary">
                <input
                  type="checkbox"
                  className={clsx(checkboxClass, 'mt-0.5')}
                  checked={agreementSigned}
                  onChange={(e) => setAgreementSigned(e.target.checked)}
                />
                <span>
                  The household has accepted “{agreement.title}” (version {agreement.version}).
                  <span className="block text-[12px] text-fg-muted">
                    Leave unticked to book without it — that is an override, recorded with your reason.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {preview && (
          <div className="mt-4 space-y-3 border-t border-line/60 pt-3">
            <p className="text-[13px] text-fg-secondary">
              {isZeroMoney(preview.fee)
                ? 'No fee.'
                : `${formatMoney(preview.fee)} will be charged to the home’s account.`}
              {preview.fee_refundable_until_local &&
                ` The household can cancel for a full reversal until ${formatDayLabel(
                  preview.fee_refundable_until_local.slice(0, 10),
                  thisYear
                )}, ${formatMinute(localMinute(preview.fee_refundable_until_local) ?? 0)}.`}
              {preview.deposit &&
                !isZeroMoney(preview.deposit) &&
                ` A ${formatMoney(preview.deposit)} refundable deposit is charged with it and held until the booking is settled.`}
            </p>
            <FeeBreakdown lines={preview.fee_breakdown} className="max-w-xs" />
            <RuleViolationsPanel
              idPrefix="create-reservation"
              violations={violations}
              ticked={ticked}
              onTickedChange={setTicked}
              reason={overrideReason}
              onReasonChange={setOverrideReason}
            />
          </div>
        )}

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-line/60 pt-4">
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={!canSubmit}>
            {saving
              ? 'Booking…'
              : ticked.length
                ? 'Book with overrides'
                : 'Book'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default CreateReservationModal;
